import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import VoiceoverPanel from "../components/VoiceoverPanel";
import ReferenceImagesPanel from "../components/ReferenceImagesPanel";
import SeriesStatusButton from "../components/SeriesStatusButton";
import ThumbnailsPanel from "../components/ThumbnailsPanel";
import ISO6391 from "iso-639-1";
import {
  generateScript,
  translateToEnglish,
  getUserSeries,
  updateScript,
  getUserScripts,
} from "../services/scriptService";
import { ScriptResponse, SeriesResponse } from "../types";
import TrialBanner from "../components/TrialBanner";
import ShotListPanel from "../components/ShotListPanel";
import { getCurrentUser } from "../services/authService";

// SERIES: used when a user continues a STORY series without writing a prompt
const DEFAULT_CONTINUE_STORY_PROMPT =
  "Continue the story naturally from exactly where the previous part ended. Keep the same characters, places and tone, raise the stakes, add a surprising twist, and end on a strong cliffhanger.";

// SERIES: used when a user continues an EXPLAINER series without writing a prompt
const DEFAULT_CONTINUE_EXPLAINER_PROMPT =
  "Continue this educational series with the next most useful topic. Build on what earlier parts taught, briefly remind viewers where the last part ended, and do not repeat any points already covered.";

const Dashboard = () => {
  const [topic, setTopic] = useState("");
  const [niche, setNiche] = useState("");
  const [tone, setTone] = useState("engaging");
  const [language, setLanguage] = useState("English");
  const [duration, setDuration] = useState("5");
  const [scriptFormat, setScriptFormat] = useState(""); // Default to empty string for placeholder

  // SERIES: single script, start a new series, or continue a series
  const [seriesMode, setSeriesMode] = useState<"NONE" | "NEW" | "CONTINUE">(
    "NONE",
  );
  const [selectedSeriesId, setSelectedSeriesId] = useState("");
  // FINAL PART: optional tick box when continuing a series
  const [finalPart, setFinalPart] = useState(false);
  const [seriesList, setSeriesList] = useState<SeriesResponse[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [script, setScript] = useState<ScriptResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [showTranslation, setShowTranslation] = useState(false);
  const [translatedData, setTranslatedData] = useState<{
    title: string;
    scriptContent: string;
    description: string;
    hashtags: string;
    thumbnailIdeas: string;
  } | null>(null);

  // EDIT: editing the generated script
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editText, setEditText] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState("");
  const [editNotice, setEditNotice] = useState("");
  const [editVersion, setEditVersion] = useState(0);

  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  const isStoryFormat = scriptFormat === "AI_FILM" || scriptFormat === "SHORTS";
  const isContinuing = scriptFormat !== "" && seriesMode === "CONTINUE";

  // SERIES: load the user's series for the dropdown
  const loadSeries = async () => {
    try {
      const data = await getUserSeries();
      setSeriesList(data);
    } catch {}
  };

  useEffect(() => {
    loadSeries();
  }, []);

  // REMEMBER SCREEN: keep the Dashboard as it was after a refresh (this browser tab only)
  const storageKey = user
    ? `tubescript-dashboard-${(user as any).email || (user as any).id || (user as any).fullName || "me"}`
    : "";
  const [restored, setRestored] = useState(false);

  // Bring back the saved form and script once, when the page opens
  useEffect(() => {
    if (!storageKey || restored) return;
    const restore = async () => {
      try {
        const raw = sessionStorage.getItem(storageKey);
        if (raw) {
          const saved = JSON.parse(raw);
          setTopic(saved.topic || "");
          setNiche(saved.niche || "");
          setTone(saved.tone || "engaging");
          setLanguage(saved.language || "English");
          setScriptFormat(saved.scriptFormat || "");
          setDuration(saved.duration || "5");
          setSeriesMode(saved.seriesMode || "NONE");
          setSelectedSeriesId(saved.selectedSeriesId || "");
          if (saved.scriptId) {
            const all = await getUserScripts();
            const found = all.find((s) => s.id === saved.scriptId);
            if (found) setScript(found);
          }
        }
      } catch {
        // If anything goes wrong, just start with a clean Dashboard
      } finally {
        setRestored(true);
      }
    };
    restore();
  }, [storageKey, restored]);

  // Save the form and the current script whenever they change
  useEffect(() => {
    if (!storageKey || !restored) return;
    try {
      sessionStorage.setItem(
        storageKey,
        JSON.stringify({
          topic,
          niche,
          tone,
          language,
          duration,
          scriptFormat,
          seriesMode,
          selectedSeriesId,
          scriptId: script?.id ?? null,
        }),
      );
    } catch {
      // Storage full or blocked: nothing to do
    }
  }, [
    storageKey,
    restored,
    topic,
    niche,
    tone,
    language,
    duration,
    scriptFormat,
    seriesMode,
    selectedSeriesId,
    script,
  ]);

  const handleFormatChange = (format: string) => {
    setScriptFormat(format);
    if (format === "SHORTS") {
      setDuration("60_SEC");
    } else {
      setDuration("5");
    }
    // SERIES: start fresh when the format changes
    setSeriesMode("NONE");
    setSelectedSeriesId("");
    setFinalPart(false);
  };

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // SERIES: check the series choice before generating
    if (seriesMode === "CONTINUE" && !selectedSeriesId) {
      setError("Please choose the series you want to continue.");
      return;
    }

    // The prompt is required, except when continuing a series
    if (!topic.trim() && !isContinuing) {
      setError("Please enter your video topic or story prompt.");
      return;
    }

    setScript(null);
    setTranslatedData(null);
    setShowTranslation(false);
    setIsEditing(false);
    setEditError("");
    setEditNotice("");
    setLoading(true);
    try {
      // The backend names a new series after the title it generates
      const seriesOptions =
        seriesMode === "NEW"
          ? { newSeriesTitle: "New series" }
          : seriesMode === "CONTINUE"
            ? {
                seriesId: Number(selectedSeriesId),
                finalPart: finalPart || undefined,
              }
            : undefined;

      // SERIES: if continuing with no prompt, let the app continue on its own
      const defaultContinuePrompt = isStoryFormat
        ? DEFAULT_CONTINUE_STORY_PROMPT
        : DEFAULT_CONTINUE_EXPLAINER_PROMPT;
      const finalTopic =
        isContinuing && !topic.trim()
          ? finalPart
            ? "Continue the story from exactly where the previous part ended, and bring it to its ending."
            : defaultContinuePrompt
          : topic.trim();

      const response = await generateScript(
        finalTopic,
        niche,
        tone,
        language,
        duration,
        scriptFormat,
        seriesOptions,
      );
      setScript(response);
      setFinalPart(false);

      // SERIES: after a series part is made, switch to "Continue" for the next part
      if (seriesMode !== "NONE" && response.seriesId) {
        await loadSeries();
        setSeriesMode("CONTINUE");
        setSelectedSeriesId(String(response.seriesId));
      }

      const updatedUser = await getCurrentUser();
      updateUser(updatedUser);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "Something went wrong. Please try again.",
      );
      try {
        const updatedUser = await getCurrentUser();
        updateUser(updatedUser);
      } catch {}
    } finally {
      setLoading(false);
    }
  };

  // EDIT: open the editor with the current title and script
  const handleStartEdit = () => {
    if (!script) return;
    setEditTitle(script.title);
    setEditText(script.scriptContent);
    setEditError("");
    setEditNotice("");
    setIsEditing(true);
  };

  // EDIT: save the changes
  const handleSaveEdit = async () => {
    if (!script) return;
    if (!editText.trim()) {
      setEditError("The script cannot be empty.");
      return;
    }
    setSavingEdit(true);
    setEditError("");
    try {
      const hadShotList = !!script.shotList;
      const updated = await updateScript(script.id, editText, editTitle);
      setScript(updated);
      setTranslatedData(null);
      setShowTranslation(false);
      setIsEditing(false);
      setEditVersion((v) => v + 1);
      setEditNotice(
        hadShotList && !updated.shotList
          ? "Your changes are saved. Your script changed, so please generate the shot list again to match it."
          : "Your changes are saved.",
      );
    } catch (err: any) {
      setEditError(
        err.response?.data?.message ||
          "Could not save your changes. Please try again.",
      );
    } finally {
      setSavingEdit(false);
    }
  };

  const handleTranslate = async () => {
    if (showTranslation) {
      setShowTranslation(false);
      return;
    }
    if (translatedData) {
      setShowTranslation(true);
      return;
    }
    if (!script) return;
    setTranslating(true);
    try {
      const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

      const title = await translateToEnglish(script.title);
      await delay(500);
      const scriptContent = await translateToEnglish(script.scriptContent);
      await delay(500);
      const description = await translateToEnglish(script.description);
      await delay(500);
      const hashtags = await translateToEnglish(script.hashtags);
      await delay(500);
      const thumbnailIdeas = await translateToEnglish(script.thumbnailIdeas);

      setTranslatedData({
        title,
        scriptContent,
        description,
        hashtags,
        thumbnailIdeas,
      });
      setShowTranslation(true);
    } catch (err) {
      console.error(err);
    } finally {
      setTranslating(false);
    }
  };

  const handleCopy = () => {
    if (script) {
      const fullScript = `
TITLE: ${script.title}

SCRIPT:
${script.scriptContent}

DESCRIPTION:
${script.description}

HASHTAGS:
${script.hashtags}

THUMBNAIL IDEAS:
${script.thumbnailIdeas}
      `.trim();
      navigator.clipboard.writeText(fullScript);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const niches = [
    "Health & Wellness",
    "Technology",
    "Finance",
    "Food & Cooking",
    "Travel",
    "Education",
    "Fitness",
    "Entertainment",
    "Fashion",
    "Gaming",
    "Music",
    "Business",
    "Motivation",
    "Science",
    "Other",
  ];

  const tones = [
    { value: "engaging", label: "🎯 Engaging" },
    { value: "funny", label: "😄 Funny" },
    { value: "formal", label: "👔 Formal" },
    { value: "casual", label: "😊 Casual" },
    { value: "educational", label: "📚 Educational" },
    { value: "motivational", label: "💪 Motivational" },
  ];

  // Your main languages always come first, in this order
  const pinnedLanguages = [
    "English",
    "Yoruba",
    "Igbo",
    "Hausa",
    "French",
    "Pidgin English",
    "Spanish",
    "Portuguese",
    "Arabic",
    "Hindi",
  ];

  // Then every other world language, alphabetically
  const languages = [
    ...pinnedLanguages,
    ...ISO6391.getAllNames()
      .filter((name) => !pinnedLanguages.includes(name))
      .sort((a, b) => a.localeCompare(b)),
  ];

  const isAgency = user?.plan === "AGENCY";

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Navbar />
      <TrialBanner />

      <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-10">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold">
            Generate Your Script 🎬
          </h1>
          <p className="text-gray-400 mt-2 text-sm md:text-base">
            Enter your topic and niche to generate a professional script!
          </p>
        </div>

        {/* Plan Info */}
        <div
          className={`rounded-xl p-4 mb-6 md:mb-8 border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 ${
            user?.plan === "FREE"
              ? "bg-gray-800 border-gray-700"
              : "bg-purple-900 border-purple-700"
          }`}
        >
          <div>
            <span
              className={`text-sm font-semibold px-2 py-1 rounded-full ${
                user?.plan === "AGENCY"
                  ? "bg-yellow-500 text-black"
                  : user?.plan === "PRO"
                    ? "bg-purple-500 text-white"
                    : "bg-gray-700 text-gray-300"
              }`}
            >
              {user?.plan} PLAN
            </span>
            {user?.plan === "FREE" && (
              <span className="text-gray-400 text-sm ml-3">
                {user?.scriptsUsed}/4 scripts used this month
              </span>
            )}
            {user?.plan === "PRO" && (
              <span className="text-gray-400 text-sm ml-3">
                {user?.scriptsUsed}/10 scripts used this month
              </span>
            )}
            {user?.plan === "AGENCY" && (
              <span className="text-purple-300 text-sm ml-3">
                Unlimited scripts
              </span>
            )}
          </div>
          {user?.plan !== "AGENCY" && (
            <button
              onClick={() => navigate("/pricing")}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm transition w-full sm:w-auto"
            >
              Upgrade
            </button>
          )}
        </div>

        {/* Trial Warning */}
        {user?.trialUsed && !user?.isPaid && user?.plan === "AGENCY" && (
          <div className="bg-purple-900 border border-purple-600 rounded-xl p-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <p className="text-purple-300 font-semibold text-sm">
                You are on a FREE TRIAL — Enjoy all Agency features!
              </p>
              <p className="text-purple-400 text-xs mt-1">
                You have{" "}
                <strong>
                  {2 - (user?.trialScriptsUsed || 0)} script
                  {2 - (user?.trialScriptsUsed || 0) !== 1 ? "s" : ""}
                </strong>{" "}
                remaining in your trial. After that you'll need to upgrade!
              </p>
            </div>
            <button
              onClick={() => navigate("/pricing")}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm transition w-full sm:w-auto"
            >
              Upgrade
            </button>
          </div>
        )}

        {/* Trial Expired Warning */}
        {user?.trialUsed && !user?.isPaid && user?.plan === "FREE" && (
          <div className="bg-red-900 border border-red-700 rounded-xl p-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <p className="text-red-300 font-semibold text-sm">
                Your free trial has ended!
              </p>
              <p className="text-red-400 text-xs mt-1">
                You now have 4 free scripts per month. Upgrade to unlock Agency
                features again!
              </p>
            </div>
            <button
              onClick={() => navigate("/pricing")}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm transition w-full sm:w-auto"
            >
              Upgrade Now
            </button>
          </div>
        )}

        {/* Form */}
        <div className="bg-gray-800 rounded-2xl p-4 md:p-8 border border-gray-700 mb-8">
          <form onSubmit={handleGenerate} className="space-y-6">
            {/* 1. Video Topic / Detailed Story Prompt */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-gray-300 text-sm font-medium">
                  Video Topic / Detailed Story Prompt{" "}
                  {isContinuing ? "(optional)" : "*"}
                </label>
                <span className="text-xs text-gray-500">
                  {topic.length} / 2000 characters
                </span>
              </div>
              <textarea
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder={
                  isContinuing
                    ? isStoryFormat
                      ? "Optional: describe what should happen in this part, or leave empty and the story continues on its own..."
                      : "Optional: name the topic for this part, or leave empty and the app picks the next topic..."
                    : "Type a detailed story premise, plot summary, or specific instructions for your video script..."
                }
                required={!isContinuing}
                rows={4}
                maxLength={2000}
                className="w-full bg-gray-900 border border-gray-700 text-white p-4 rounded-xl focus:outline-none focus:border-purple-500 transition resize-y text-sm md:text-base leading-relaxed"
              />
              <p className="text-xs text-gray-400 mt-1">
                {isContinuing
                  ? isStoryFormat
                    ? '💡 Leave this empty to continue the story automatically, ending on a cliffhanger. For the last part, tick "This is the final part" below.'
                    : "💡 Leave this empty and the app picks the next topic without repeating earlier parts, or type the topic you want covered next."
                  : "💡 Tip: For AI Movies or Shorts, feel free to paste a full story outline or specific character details here!"}
              </p>
            </div>

            {/* 2. Script Format / Mode Dropdown */}
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Script Format / Mode *
              </label>
              <select
                value={scriptFormat}
                onChange={(e) => handleFormatChange(e.target.value)}
                required
                className="w-full bg-gray-900 border border-gray-700 text-white px-4 py-3 rounded-xl focus:outline-none focus:border-purple-500 transition"
              >
                <option value="">Select script format...</option>
                <option value="EXPLAINER">Explainer</option>
                <option value="AI_FILM">AI Movie / Drama</option>
                <option value="SHORTS">YouTube Short</option>
              </select>
            </div>

            {/* SERIES: Story Type, for every format */}
            {scriptFormat !== "" && (
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  {isStoryFormat ? "Story Type" : "Video Type"}
                </label>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    onClick={() => setSeriesMode("NONE")}
                    className={`flex-1 py-2 rounded-lg text-sm transition ${
                      seriesMode === "NONE"
                        ? "bg-purple-600 text-white font-semibold"
                        : "bg-gray-900 text-gray-400 hover:bg-gray-700"
                    }`}
                  >
                    Single script
                  </button>
                  <button
                    type="button"
                    onClick={() => setSeriesMode("NEW")}
                    className={`flex-1 py-2 rounded-lg text-sm transition ${
                      seriesMode === "NEW"
                        ? "bg-purple-600 text-white font-semibold"
                        : "bg-gray-900 text-gray-400 hover:bg-gray-700"
                    }`}
                  >
                    Start a new series
                  </button>
                  <button
                    type="button"
                    onClick={() => setSeriesMode("CONTINUE")}
                    disabled={seriesList.length === 0}
                    className={`flex-1 py-2 rounded-lg text-sm transition disabled:opacity-40 disabled:cursor-not-allowed ${
                      seriesMode === "CONTINUE"
                        ? "bg-purple-600 text-white font-semibold"
                        : "bg-gray-900 text-gray-400 hover:bg-gray-700"
                    }`}
                  >
                    Continue a series
                  </button>
                </div>

                {seriesMode === "NEW" && (
                  <p className="text-xs text-gray-400 mt-2">
                    {isStoryFormat
                      ? "This script becomes Part 1, and the series is named after the film title that is generated. Later parts will remember the characters, places and story so far."
                      : "This script becomes Part 1, and the series is named after the title that is generated. Later parts will remember what was already covered and never repeat it."}
                  </p>
                )}

                {seriesMode === "CONTINUE" && (
                  <div className="mt-3">
                    <select
                      value={selectedSeriesId}
                      onChange={(e) => setSelectedSeriesId(e.target.value)}
                      className="w-full bg-gray-900 border border-gray-700 text-white px-4 py-3 rounded-xl focus:outline-none focus:border-purple-500 transition"
                    >
                      <option value="">Choose a series...</option>
                      {seriesList.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.title.replace(/\s*[–-]?\s*Part\s*\d+\s*$/i, "")}{" "}
                          (Part {s.partsCount} done, Part {s.partsCount + 1}{" "}
                          next)
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-400 mt-1">
                      Everything from earlier parts is added automatically. You
                      can leave the prompt above empty.
                    </p>
                    {/* FINAL PART: optional; with an empty prompt, the app writes the ending itself */}
                    <label className="flex items-start gap-2 cursor-pointer mt-3">
                      <input
                        type="checkbox"
                        checked={finalPart}
                        onChange={(e) => setFinalPart(e.target.checked)}
                        className="mt-1 w-4 h-4 accent-purple-500"
                      />
                      <span className="text-sm text-gray-300">
                        This is the final part
                        <span className="block text-xs text-gray-500">
                          The story will end properly and the series will close.
                          Leave the prompt empty to let the app write the
                          ending, or describe how you want it to end.
                        </span>
                      </span>
                    </label>
                  </div>
                )}
              </div>
            )}

            {/* 3. Niche / Category */}
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Niche / Category *
              </label>
              <select
                value={niche}
                onChange={(e) => setNiche(e.target.value)}
                required
                className="w-full bg-gray-900 border border-gray-700 text-white px-4 py-3 rounded-xl focus:outline-none focus:border-purple-500 transition"
              >
                <option value="">Select your niche...</option>
                {niches.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>

            {/* 4. Duration */}
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Video Duration
              </label>
              <div className="flex gap-3">
                {scriptFormat === "SHORTS" ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setDuration("60_SEC")}
                      className={`flex-1 py-2 rounded-lg text-sm transition ${
                        duration === "60_SEC"
                          ? "bg-purple-600 text-white font-semibold"
                          : "bg-gray-900 text-gray-400 hover:bg-gray-700"
                      }`}
                    >
                      60 Seconds
                    </button>
                    <button
                      type="button"
                      onClick={() => setDuration("3_MIN")}
                      className={`flex-1 py-2 rounded-lg text-sm transition ${
                        duration === "3_MIN"
                          ? "bg-purple-600 text-white font-semibold"
                          : "bg-gray-900 text-gray-400 hover:bg-gray-700"
                      }`}
                    >
                      3 Minutes
                    </button>
                  </>
                ) : (
                  <>
                    {["5", "10"].map((d) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setDuration(d)}
                        className={`flex-1 py-2 rounded-lg text-sm transition ${
                          duration === d
                            ? "bg-purple-600 text-white font-semibold"
                            : "bg-gray-900 text-gray-400 hover:bg-gray-700"
                        }`}
                      >
                        {d} minutes
                      </button>
                    ))}
                  </>
                )}
              </div>
            </div>

            {/* Agency Features */}
            {isAgency && (
              <div className="border border-yellow-600 rounded-xl p-4 space-y-4">
                <h3 className="text-yellow-400 font-semibold text-sm">
                  ⭐ Agency Features
                </h3>

                {/* Tone */}
                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">
                    Script Tone
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {tones.map((t) => (
                      <button
                        key={t.value}
                        type="button"
                        onClick={() => setTone(t.value)}
                        className={`py-2 px-3 rounded-lg text-sm transition ${
                          tone === t.value
                            ? "bg-yellow-500 text-black font-semibold"
                            : "bg-gray-900 text-gray-400 hover:bg-gray-700"
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Language */}
                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">
                    Script Language
                  </label>
                  <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    className="w-full bg-gray-900 border border-gray-700 text-white px-4 py-3 rounded-xl focus:outline-none focus:border-yellow-500 transition"
                  >
                    {languages.map((l) => (
                      <option key={l} value={l}>
                        {l}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-900 border border-red-700 text-red-300 px-4 py-3 rounded-lg text-sm">
                {error}
                {error.includes("limit") && (
                  <button
                    onClick={() => navigate("/pricing")}
                    className="ml-2 underline text-purple-400"
                  >
                    Upgrade now
                  </button>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-purple-900 disabled:cursor-not-allowed text-white py-4 rounded-xl font-semibold text-lg transition"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8z"
                    />
                  </svg>
                  Generating your script...
                </span>
              ) : (
                "⚡ Generate Script"
              )}
            </button>
          </form>
        </div>

        {/* Generated Script */}
        {script && (
          <div className="bg-gray-800 rounded-2xl p-4 md:p-8 border border-purple-700 space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-purple-400">
                  ✅ Script Generated!
                </h2>
                {/* SERIES: show which series and part this is */}
                {script.seriesTitle && (
                  <p className="text-sm text-gray-400 mt-1">
                    {script.seriesTitle}, Part {script.partNumber}
                  </p>
                )}
              </div>
              {!isEditing && (
                <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                  {language !== "English" && (
                    <button
                      onClick={handleTranslate}
                      disabled={translating}
                      className="flex-1 sm:flex-none bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm transition"
                    >
                      {translating
                        ? "Translating..."
                        : showTranslation
                          ? "🙈 Hide English"
                          : "🌍 Translate to English"}
                    </button>
                  )}
                  {/* EDIT: open the editor */}
                  <button
                    onClick={handleStartEdit}
                    className="flex-1 sm:flex-none bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm transition"
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={handleCopy}
                    className="flex-1 sm:flex-none bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm transition"
                  >
                    {copied ? "✅ Copied!" : "📋 Copy All"}
                  </button>
                </div>
              )}
            </div>

            {/* EDIT: saved message */}
            {editNotice && !isEditing && (
              <div className="bg-green-900 border border-green-700 text-green-300 px-4 py-3 rounded-lg text-sm">
                {editNotice}
              </div>
            )}

            {/* Title */}
            <div className="bg-gray-900 rounded-xl p-4 border border-gray-700">
              <h3 className="text-gray-400 text-xs font-semibold uppercase mb-2">
                🎯 Video Title
              </h3>
              {isEditing ? (
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  maxLength={255}
                  className="w-full bg-gray-800 border border-gray-600 text-white px-3 py-2 rounded-lg focus:outline-none focus:border-purple-500 transition"
                />
              ) : (
                <p className="text-white font-semibold text-base md:text-lg">
                  {script.title}
                </p>
              )}
              {!isEditing && showTranslation && translatedData && (
                <div className="mt-3 pt-3 border-t border-gray-700">
                  <h4 className="text-blue-400 text-xs font-semibold uppercase mb-1">
                    🇬🇧 English:
                  </h4>
                  <p className="text-gray-300 font-semibold">
                    {translatedData.title}
                  </p>
                </div>
              )}
            </div>

            {/* Script */}
            <div className="bg-gray-900 rounded-xl p-4 border border-gray-700">
              <h3 className="text-gray-400 text-xs font-semibold uppercase mb-2">
                📝 Script
              </h3>
              {isEditing ? (
                <>
                  <textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    rows={20}
                    className="w-full bg-gray-800 border border-gray-600 text-white p-3 rounded-lg focus:outline-none focus:border-purple-500 transition resize-y text-sm leading-relaxed"
                  />
                  {editError && (
                    <div className="bg-red-900 border border-red-700 text-red-300 px-4 py-2 rounded-lg text-sm mt-3">
                      {editError}
                    </div>
                  )}
                  <div className="flex gap-3 mt-3">
                    <button
                      onClick={() => {
                        setIsEditing(false);
                        setEditError("");
                      }}
                      disabled={savingEdit}
                      className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-3 rounded-xl text-sm transition"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveEdit}
                      disabled={savingEdit}
                      className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-900 text-white py-3 rounded-xl font-semibold text-sm transition"
                    >
                      {savingEdit ? "Saving..." : "Save changes"}
                    </button>
                  </div>
                </>
              ) : (
                <p className="text-gray-300 whitespace-pre-line leading-relaxed text-sm md:text-base">
                  {script.scriptContent}
                </p>
              )}
              {!isEditing && showTranslation && translatedData && (
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <h4 className="text-blue-400 text-xs font-semibold uppercase mb-2">
                    🇬🇧 English:
                  </h4>
                  <p className="text-gray-300 whitespace-pre-line leading-relaxed text-sm md:text-base">
                    {translatedData.scriptContent}
                  </p>
                </div>
              )}
            </div>

            {/* Description */}
            <div className="bg-gray-900 rounded-xl p-4 border border-gray-700">
              <h3 className="text-gray-400 text-xs font-semibold uppercase mb-2">
                📄 Description
              </h3>
              <p className="text-gray-300 text-sm md:text-base">
                {script.description}
              </p>
              {showTranslation && translatedData && (
                <div className="mt-3 pt-3 border-t border-gray-700">
                  <h4 className="text-blue-400 text-xs font-semibold uppercase mb-1">
                    🇬🇧 English:
                  </h4>
                  <p className="text-gray-300 text-sm md:text-base">
                    {translatedData.description}
                  </p>
                </div>
              )}
            </div>

            {/* Hashtags */}
            <div className="bg-gray-900 rounded-xl p-4 border border-gray-700">
              <h3 className="text-gray-400 text-xs font-semibold uppercase mb-2">
                #️⃣ Hashtags
              </h3>
              <p className="text-purple-400 text-sm md:text-base">
                {script.hashtags}
              </p>
              {showTranslation && translatedData && (
                <div className="mt-3 pt-3 border-t border-gray-700">
                  <h4 className="text-blue-400 text-xs font-semibold uppercase mb-1">
                    🇬🇧 English:
                  </h4>
                  <p className="text-purple-300 text-sm md:text-base">
                    {translatedData.hashtags}
                  </p>
                </div>
              )}
            </div>

            {/* Thumbnail Ideas */}
            <div className="bg-gray-900 rounded-xl p-4 border border-gray-700">
              <h3 className="text-gray-400 text-xs font-semibold uppercase mb-2">
                🖼️ Thumbnail Ideas
              </h3>
              <p className="text-gray-300 text-sm md:text-base">
                {script.thumbnailIdeas}
              </p>
              {showTranslation && translatedData && (
                <div className="mt-3 pt-3 border-t border-gray-700">
                  <h4 className="text-blue-400 text-xs font-semibold uppercase mb-1">
                    🇬🇧 English:
                  </h4>
                  <p className="text-gray-300 text-sm md:text-base">
                    {translatedData.thumbnailIdeas}
                  </p>
                </div>
              )}
            </div>

            {/* FINISHED SERIES: finish or reopen this series */}
            <SeriesStatusButton
              key={`series-${script.id}`}
              script={script}
              onChange={loadSeries}
            />

            {/* THUMBNAIL PICTURES (optional; the text ideas above always stay) */}
            <ThumbnailsPanel key={`thumbs-${script.id}`} script={script} />

            {/* VOICEOVER (optional; refreshes after an edit) */}
            <VoiceoverPanel
              key={`voice-${script.id}-${editVersion}`}
              script={script}
            />

            {/* REFERENCE IMAGES: one picture per character (refreshes after an edit) */}
            <ReferenceImagesPanel
              key={`refs-${script.id}-${editVersion}`}
              script={script}
            />

            {/* SHOT LIST (refreshes after an edit, so it always matches the script) */}
            <ShotListPanel
              key={`${script.id}-${editVersion}`}
              script={script}
            />

            <button
              onClick={() => navigate("/my-scripts")}
              className="w-full border border-purple-600 hover:bg-purple-900 text-purple-400 py-3 rounded-xl font-semibold transition"
            >
              View All My Scripts →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
